import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';

import { type WorkspaceAgentMentionRowItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import {
  toWorkspaceAgentBoardListQuerySpec,
  type WorkspaceAgentBoardListQueryNode,
  type WorkspaceAgentBoardListQuerySpec,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentBoardListQuery';
import { WORKSPACE_AGENT_MENTION_CHILD_PREFETCH_DELAY_MS } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';
import { isWorkspaceAgentMentionTreeItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';

export type ToWorkspaceAgentMentionPrefetchNodeParams = {
  item: WorkspaceAgentMentionRowItem | undefined;
  listQuerySpecs: readonly WorkspaceAgentBoardListQuerySpec[];
  onToggleExpanded: ((nodeId: string, isExpanded: boolean) => void) | undefined;
  query: string;
};

export type UseWorkspaceAgentMentionChildPrefetchParams = Omit<ToWorkspaceAgentMentionPrefetchNodeParams, 'item'> & {
  highlightedIndex: number;
  items: readonly WorkspaceAgentMentionRowItem[];
  workspaceId: string | undefined;
};

export type UseWorkspaceAgentMentionChildPrefetchResult = {
  prefetchSpec: WorkspaceAgentBoardListQuerySpec | undefined;
};

/**
 * A collapsed expandable tree row whose first children page is worth fetching ahead of expand.
 * Hover and keyboard share the highlighted index, so both are the same signal. Skip rows already
 * mounted as expanded observers — a second hook with the same query key still remounts.
 */
export const toWorkspaceAgentMentionPrefetchNode = ({
  item,
  listQuerySpecs,
  onToggleExpanded,
  query,
}: ToWorkspaceAgentMentionPrefetchNodeParams): WorkspaceAgentBoardListQueryNode | undefined => {
  if (!onToggleExpanded || query !== '' || !item || !isWorkspaceAgentMentionTreeItem(item)) {
    return undefined;
  }

  if (!item.hasChildren || item.isExpanded) {
    return undefined;
  }

  if (listQuerySpecs.some((spec) => spec.nodeId === item.id)) {
    return undefined;
  }

  return { id: item.id, kind: item.kind };
};

/**
 * Debounces the highlighted collapsed row, then hands the popup the same observer spec expand would
 * mount. Re-checks after the delay so an open that landed during it does not keep a duplicate.
 */
export const useWorkspaceAgentMentionChildPrefetch = ({
  highlightedIndex,
  items,
  listQuerySpecs,
  onToggleExpanded,
  query,
  workspaceId,
}: UseWorkspaceAgentMentionChildPrefetchParams): UseWorkspaceAgentMentionChildPrefetchResult => {
  const highlightedPrefetchNodeId =
    toWorkspaceAgentMentionPrefetchNode({
      item: items[highlightedIndex],
      listQuerySpecs,
      onToggleExpanded,
      query,
    })?.id ?? null;

  const [debouncedPrefetchNodeId] = useDebounce(
    highlightedPrefetchNodeId,
    WORKSPACE_AGENT_MENTION_CHILD_PREFETCH_DELAY_MS,
  );

  const prefetchSpec = useMemo(() => {
    if (!workspaceId || !debouncedPrefetchNodeId) {
      return undefined;
    }

    const item = items.find((row) => row.id === debouncedPrefetchNodeId);
    const node = toWorkspaceAgentMentionPrefetchNode({
      item,
      listQuerySpecs,
      onToggleExpanded,
      query,
    });

    return node ? toWorkspaceAgentBoardListQuerySpec(node, workspaceId) : undefined;
  }, [debouncedPrefetchNodeId, items, listQuerySpecs, onToggleExpanded, query, workspaceId]);

  return useMemo(() => ({ prefetchSpec }), [prefetchSpec]);
};
